<template>
    <table class="pricing-table" :class="pricingTableModifiers">
        <LoadingIndicator v-if="pricesBeingCalculated" class="pricing-table__loading-indicator" />
        <!-- RENTER (and admin) -->
        <tbody v-if="showRenterVersion">
            <tr v-if="isAdmin && roleOverride !== 'owner' && roleOverride !== 'renter'">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <th>- As an admin, you are viewing the renter pricing table -</th>
            </tr>
            <tr v-if="showTitle">
                <th class="pricing-table__heading">
                    {{ $t('pricing.total_price_title') }}
                    <svg-icon v-tooltip="$t('pricing.total_price_tooltip')" name="info-outline" />
                </th>
            </tr>
            <tr v-if="showPricePerNight" class="pricing-table__price-per-night">
                <th class="pricing-table__heading">
                    {{ $t('pricing.price_per_night') }}
                </th>
                <td>
                    {{ pricePerNight | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                </td>
            </tr>
            <!-- Renter order items -->
            <tr v-for="item in renterOrderItems" :key="item.id">
                <th>
                    {{ item.description || getOrderItemDescription(item) }}
                </th>
                <td v-if="item.subTotal">
                    {{ item.subTotal | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                </td>
                <td v-else-if="item.value">
                    {{ item.value }}
                </td>
            </tr>
            <!-- Renter discount -->
            <tr v-if="discountTotal > 0">
                <th>{{ $t('pricing.discount') }}</th>
                <td>
                    {{ (-1 * discountTotal) | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                </td>
            </tr>
        </tbody>
        <!-- OWNER -->
        <tbody v-else>
            <tr>
                <th class="pricing-table__heading">
                    {{ $t('pricing.earnings_overview') }}
                </th>
            </tr>
            <!-- Owner order items -->
            <tr v-for="item in ownerOrderItems" :key="item.id">
                <th>
                    {{ getOrderItemDescription(item) }}
                </th>
                <td>
                    {{ item.ownersSubTotal | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                </td>
            </tr>
            <!-- Owner discount -->
            <tr v-if="ownerDiscount">
                <th>{{ $t('pricing.discount') }}</th>
                <td>
                    {{ (-1 * ownerDiscount.amount) | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                </td>
            </tr>
            <!-- Owner deposit handling transaction fee -->
            <tr v-if="order.deposit && order.deposit.transactionFeeAmount > 0">
                <th>{{ $t('pricing.deposit_handling_transaction_fee') }}</th>
                <td>
                    {{
                        (-1 * order.deposit.transactionFeeAmount)
                            | price
                            | formatAsPrice(order.currency, 2, convertCurrency)
                    }}
                </td>
            </tr>
        </tbody>
        <tfoot>
            <template v-if="!isOwner">
                <tr class="pricing-table__total">
                    <th>
                        {{ $t('pricing.rental_fee_total') }}
                    </th>
                    <td>
                        {{ order.total | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                    </td>
                </tr>
                <tr class="pricing-table__specification pricing-table__vat">
                    <th>
                        {{ $t('pricing.tax') }}
                    </th>
                    <td>{{ taxAmount }}</td>
                </tr>
                <tr v-if="toPayNow" class="pricing-table__to-pay-now">
                    <th>{{ $t('pages.booking_request.to_be_charged') }}</th>
                    <td>
                        {{ toPayNow | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                    </td>
                </tr>
            </template>

            <template v-if="isOwner">
                <tr class="pricing-table__earnings">
                    <th>{{ $t('pricing.you_make') }}</th>
                    <td>
                        {{ order.ownersTotal | price | formatAsPrice(order.currency, 2, convertCurrency) }}
                    </td>
                </tr>
            </template>
        </tfoot>
    </table>
</template>

<script>
import { mapGetters } from 'vuex';
import {
    LOW_SEASON_NIGHT,
    LOW_SEASON_SHORT_BOOKING_NIGHT,
    LOW_SEASON_DISCOUNTED_NIGHT,
    HIGH_SEASON_NIGHT,
    HIGH_SEASON_SHORT_BOOKING_NIGHT,
    HIGH_SEASON_DISCOUNTED_NIGHT,
    PLATFORM_INSURANCE,
    CLEANING,
    INSURANCE_ADDON_TYPE,
    ADDITIONAL_DRIVER,
    ID_VERIFICATION,
    INSURANCE_ADDON,
    TYPE_EQUIPMENT_ADDON,
    TYPE_SERVICE_ADDON,
    OMOCOM_BASIC,
    OMOCOM_PLUS,
    OMOCOM_COMPLETE,
} from '~/config/productTypes';

import LoadingIndicator from '~/components/LoadingIndicator.vue';

const NIGHT_PRODUCT_TYPES = [
    LOW_SEASON_NIGHT,
    HIGH_SEASON_NIGHT,
    LOW_SEASON_SHORT_BOOKING_NIGHT,
    HIGH_SEASON_SHORT_BOOKING_NIGHT,
    LOW_SEASON_DISCOUNTED_NIGHT,
    HIGH_SEASON_DISCOUNTED_NIGHT,
];

export default {
    components: {
        LoadingIndicator,
    },
    props: {
        numberOfNights: {
            type: Number,
            default: null,
        },
        order: {
            type: Object,
            default: () => {},
        },
        orderItems: {
            type: Array,
            default: () => [],
        },
        convertCurrency: {
            type: Boolean,
            default: true,
        },
        isBaloiseInsurance: {
            type: Boolean,
            default: false,
        },
        isOmocomInsurance: {
            type: Boolean,
            default: false,
        },
        toPayNow: {
            type: Number,
            default: null,
        },
        showTitle: {
            type: Boolean,
            default: true,
        },
        showPricePerNight: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            renterOrderItems: [],
            ownerOrderItems: [],
            platformDiscount: null,
            ownerDiscount: null,
        };
    },
    computed: {
        ...mapGetters([
            'orderDiscounts',
            'roleOverride',
            'isOwner',
            'isRenter',
            'isAdmin',
            'booking',
            'orderIsCanceled',
            'extraChargesTotalAmountIncFee',
            'isLoggedIn',
            'cartPricesLoading',
        ]),
        pricingTableModifiers() {
            return {
                'pricing-table--canceled': this.orderIsCanceled && this.isOwner,
                'pricing-table--with-to-pay-now': !!this.toPayNow,
                'pricing-table--loading': this.pricesBeingCalculated,
            };
        },
        taxAmount() {
            const { formatAsPrice, price } = this.$root.$options.filters;
            const { serviceFee, serviceFeeTaxPercentage } = this.order;
            const amount = serviceFee * (serviceFeeTaxPercentage / (1 + serviceFeeTaxPercentage));

            return formatAsPrice(price(amount), this.order.currency, 2, this.convertCurrency);
        },
        discountTotal() {
            let total = 0;

            if (this.ownerDiscount) {
                total += this.ownerDiscount.amount;
            }

            if (this.platformDiscount) {
                total += this.platformDiscount.amount;
            }

            return total;
        },
        showRenterVersion() {
            return this.isRenter || (this.isAdmin && this.roleOverride !== 'owner') || !this.isLoggedIn;
        },
        pricesBeingCalculated() {
            return this.cartPricesLoading;
        },
        pricePerNight() {
            return this.order.total / this.numberOfNights;
        },
    },
    watch: {
        orderItems: {
            handler(newVal) {
                this.setOrderItems();
            },
            deep: true,
        },
    },
    created() {
        this.setOrderItems();
        this.setOrderDiscounts();
    },
    methods: {
        setOrderItems() {
            if (this.isRenter || this.isAdmin || !this.isLoggedIn) {
                this.renterOrderItems = this.formatRenterOrderItems(this.orderItems);
            }

            if (this.isOwner || this.isAdmin) {
                // Remove all items that have ownersSubTotal = 0
                this.ownerOrderItems = this.orderItems.filter(item => item.ownersSubTotal > 0);
            }
        },

        setOrderDiscounts() {
            if (this.orderDiscounts.length > 0) {
                this.ownerDiscount = this.orderDiscounts.find(discount => discount.type === 'owner') || null;
                this.platformDiscount = this.orderDiscounts.find(discount => discount.type === 'platform_fee') || null;
            }
        },

        /**
         * Get the the percentage of the discount or the surcharge for a given product type
         *
         * @param {String} productType - The product type to get the price adjustment percentage for
         * @returns {Number} The price adjustment percentage
         */
        getItemPriceAdjustmentPercentage(productType) {
            let percentage;

            switch (productType) {
                case LOW_SEASON_SHORT_BOOKING_NIGHT:
                    percentage = this.booking.assetSnapshot.priceLowIncrease;
                    break;
                case LOW_SEASON_DISCOUNTED_NIGHT:
                    percentage = this.booking.assetSnapshot.priceLowDiscount;
                    break;
                case HIGH_SEASON_SHORT_BOOKING_NIGHT:
                    percentage = this.booking.assetSnapshot.priceHighIncrease;
                    break;
                case HIGH_SEASON_DISCOUNTED_NIGHT:
                    percentage = this.booking.assetSnapshot.priceHighDiscount;
                    break;
                default:
                    percentage = 0;
            }

            return percentage * 100;
        },

        /**
         * Get the description for an order item
         * @param {Object} item - The order item
         * @returns {string} The description of the order item
         */
        getOrderItemDescription(item) {
            if (item.priceType === 'extra_charge') {
                return this.$t('pricing.extra_charges');
            }

            switch (item.productType) {
                case CLEANING:
                    return this.$tc('pricing.cleaning');
                case ADDITIONAL_DRIVER:
                    return this.$tc('pricing.additional_driver');
                case ID_VERIFICATION:
                    return this.$tc('pricing.id_verification');
                case PLATFORM_INSURANCE:
                    return this.getPlatformInsuranceDescription(item);
                case INSURANCE_ADDON_TYPE:
                    return this.getInsuranceAddonDescription(item);
                case TYPE_EQUIPMENT_ADDON:
                case TYPE_SERVICE_ADDON:
                    return this.getOwnerAddonDescription(item);
                default:
                    return this.getNightDescription(item);
            }
        },

        /**
         * Get the description for platform insurance
         * @param {Object} item - The order item
         * @returns {string} The description for platform insurance
         */
        getPlatformInsuranceDescription(item) {
            if (this.isOmocomInsurance) {
                const key = item.product?.key;

                switch (key) {
                    case OMOCOM_PLUS:
                        return this.$t('pricing.omocom_plus');
                    case OMOCOM_COMPLETE:
                        return this.$t('pricing.omocom_complete');
                    case OMOCOM_BASIC:
                    default:
                        return this.$t('pricing.omocom_basic');
                }
            }

            return this.isBaloiseInsurance
                ? this.$t('pricing.baloise_insurance_fee')
                : this.$t('pricing.insurance_fee');
        },

        /**
         * Get the description for night-related items
         * @param {Object} item - The order item
         * @returns {string} The description for night-related items
         */
        getNightDescription(item) {
            const percentage = this.getItemPriceAdjustmentPercentage(item.productType);
            let description;

            switch (item.productType) {
                case LOW_SEASON_NIGHT:
                case HIGH_SEASON_NIGHT:
                    description = this.$tc('pricing.regular_booking_nights', item.quantity);
                    break;
                case LOW_SEASON_SHORT_BOOKING_NIGHT:
                case HIGH_SEASON_SHORT_BOOKING_NIGHT:
                    description = this.getSpecialPricingDescription(item, percentage, 'short');
                    break;
                case LOW_SEASON_DISCOUNTED_NIGHT:
                case HIGH_SEASON_DISCOUNTED_NIGHT:
                    description = this.getSpecialPricingDescription(item, percentage, 'discounted');
                    break;
                default:
                    description = '';
            }

            return this.appendSeasonIfNeeded(description, item);
        },

        /**
         * Get the description for special pricing nights (short or discounted)
         * @param {Object} item - The order item
         * @param {number} percentage - The price adjustment percentage
         * @param {string} pricingType - The type of special pricing ('short' or 'discounted')
         * @returns {string} The description for special pricing nights
         */
        getSpecialPricingDescription(item, percentage, pricingType) {
            if (percentage === 0) {
                return this.$tc('pricing.regular_booking_nights', item.quantity);
            }

            const key = `pricing.${pricingType}_booking_nights`;

            return this.$tc(key, item.quantity, { percentage });
        },

        /**
         * Append season information to the description if needed
         * @param {string} description - The current description
         * @param {Object} item - The order item
         * @returns {string} The description with season information appended if needed
         */
        appendSeasonIfNeeded(description, item) {
            const hasLowSeason = this.ownerOrderItems.some(i =>
                [LOW_SEASON_NIGHT, LOW_SEASON_SHORT_BOOKING_NIGHT, LOW_SEASON_DISCOUNTED_NIGHT].includes(i.productType)
            );
            const hasHighSeason = this.ownerOrderItems.some(i =>
                [HIGH_SEASON_NIGHT, HIGH_SEASON_SHORT_BOOKING_NIGHT, HIGH_SEASON_DISCOUNTED_NIGHT].includes(
                    i.productType
                )
            );

            const shouldAppendSeason = hasLowSeason && hasHighSeason;

            if (!shouldAppendSeason) {
                return description;
            }

            const isLowSeason = item.productType.startsWith('low_');
            const seasonKey = isLowSeason ? 'low_season' : 'high_season';
            const seasonDescription = this.$t(`pages.edit_vehicle_pricing.${seasonKey}`).toLowerCase();

            return `${description} (${seasonDescription})`;
        },

        /**
         * Get the description for insurance addon
         * @param {Object} item - The order item
         * @returns {string} The description for insurance addon
         */
        getInsuranceAddonDescription(item) {
            if (item.product.key === 'interior_insurance') {
                return this.isOmocomInsurance
                    ? this.$t('pricing.omocom_interior_insurance')
                    : this.$t('pricing.interior_insurance');
            }

            return this.$tc(`pricing.${item.product.key}`);
        },

        /**
         * Get the description for owner addon
         * @param {Object} item - The order item
         * @returns {string} The description for owner addon
         */
        getOwnerAddonDescription(item) {
            let description = this.$t(`pages.vehicle.${item.productType}.${item.product?.key}`);

            if (item.product?.selectedQuantity && item.product?.selectedQuantity > 1) {
                description += ` (x${item.product.selectedQuantity})`;
            }

            return description;
        },

        /**
         * Makes sure that the renter sees only one row for all nights
         * Adds deductible insurance to the rental nights amount
         * Adds a 'Insurance covered by owner' row if no PLATFORM_INSURANCE item exists
         * Adds a single consolidated extra charge row at the end if there are extra charges
         *
         * @param {Array} items - Order items to format
         * @returns {Array} The formatted items
         */
        formatRenterOrderItems(items) {
            const nightItems = items.filter(item => NIGHT_PRODUCT_TYPES.includes(item.productType));
            const otherItems = items.filter(item => !NIGHT_PRODUCT_TYPES.includes(item.productType));

            const formattedItems = [];

            if (nightItems.length > 0) {
                let nightTotal = nightItems.reduce((sum, item) => sum + item.subTotal, 0);

                const deductibleInsurance = items.find(
                    item =>
                        item.productType === INSURANCE_ADDON_TYPE &&
                        item.product.key === INSURANCE_ADDON.deductible_insurance
                );

                if (deductibleInsurance) {
                    nightTotal += deductibleInsurance.subTotal;
                }

                const mergedNightItem = {
                    id: 'merged-nights',
                    description: this.$tc('pricing.regular_booking_nights', this.numberOfNights),
                    subTotal: nightTotal,
                    quantity: this.numberOfNights,
                    productType: nightItems[0].productType,
                };
                formattedItems.push(mergedNightItem);
            }

            if (!items.find(item => item.productType === PLATFORM_INSURANCE)) {
                formattedItems.push({
                    id: 'insurance-via-owner',
                    description: this.$t('pricing.insurance_via_owner'),
                    value: this.$t('common.included'),
                });
            }

            let filteredOtherItems = otherItems.filter(
                item => !(item.productType === INSURANCE_ADDON_TYPE && item.product.key === 'deductible_insurance')
            );

            // Handle extra charge items
            const hasExtraCharges = filteredOtherItems.some(item => item.priceType === 'extra_charge');
            let extraChargeItem = null;

            if (hasExtraCharges) {
                // Remove all extra charge items
                filteredOtherItems = filteredOtherItems.filter(item => item.priceType !== 'extra_charge');
                // Create a single consolidated extra charge item|row
                extraChargeItem = {
                    id: 'extra-charges',
                    description: this.$t('pricing.extra_charges'),
                    subTotal: this.extraChargesTotalAmountIncFee,
                    productType: 'extra_charge',
                };
            }

            // Combine all items
            const result = [...formattedItems, ...filteredOtherItems];

            // Add extra charge item at the end if it exists
            if (extraChargeItem) {
                result.push(extraChargeItem);
            }

            return result;
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.pricing-table {
    position: relative;
    @include font-size(16px);

    &__heading {
        font-weight: 700;
    }

    &__price-per-night {
        @include font-size(14px);

        td,
        th {
            padding-bottom: 12px;
        }

        td {
            font-weight: 600;
        }
    }

    &__total {
        td,
        th {
            font-weight: 700;

            .pricing-table--with-to-pay-now & {
                font-weight: 600;
            }
        }
    }

    &__to-pay-now {
        border-top: 1px solid $darkest-gray;

        td,
        th {
            font-weight: 700;
            padding: 15px 0;
        }
    }

    &__vat {
        .pricing-table--with-to-pay-now & {
            td,
            th {
                padding-bottom: 20px;
            }
        }
    }

    &--loading {
        tbody,
        tfoot {
            opacity: 0.1;
        }

        tfoot {
            border: none;
        }
    }

    &__loading-indicator {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
}
</style>
